// src/hooks/useContacts.ts
import { useContext, useMemo } from "react";
import { ContactsContext } from "../context/ContactsContext";
import { divideByKey, DivideByKeyResultTypes } from "../utils";
import { IContact } from "../types/contacts";

interface UseContactsReturn {
  contacts: IContact[];
  contactsMap: Map<number, IContact>;
  contactsByPhoneMap: Map<string, IContact>;
  categorizedContacts: DivideByKeyResultTypes<IContact>[]; // DivideByKeyResultTypes[];
  totalContacts: number;
  loading: boolean;
  error: string | null;

  incrementUnread: (idChatSession: number, amount?: number) => void;
  getContacts: () => Promise<IContact[]>;
  addContact: (data: IContact) => Promise<IContact | null>;
  updateContact: (data: IContact) => Promise<IContact | null>;
  deleteContact: (id: number) => Promise<void>;
  contactsImage: (data: any) => Promise<IContact | null>;
  contactsVisit: (data: any) => Promise<IContact | null>;
  contactsMarked: (data: any) => Promise<IContact | null>;
  contactsService: (data: any) => Promise<IContact | null>;
}

export const useContacts = (): UseContactsReturn => {
  const context = useContext(ContactsContext);
  if (!context)
    throw new Error("useContacts must be used within ContactsProvider");

  const {
    contacts,
    contactsMap,
    contactsByPhoneMap,
    loading,
    error,
    incrementUnread,
    getContacts,
    addContact,
    updateContact,
    deleteContact,
    contactsImage,
    contactsVisit,
    contactsMarked,
    contactsService,
  } = context;

  const categorizedContacts = useMemo(
    () => divideByKey("name", contacts),
    [contacts],
  );
  const totalContacts = contacts.length;

  return {
    contacts,
    contactsMap,
    contactsByPhoneMap,
    categorizedContacts,
    totalContacts,
    loading,
    error,
    incrementUnread,
    getContacts,
    addContact,
    updateContact,
    deleteContact,
    contactsImage,
    contactsVisit,
    contactsMarked,
    contactsService,
  };
};
